import type { TaskStatus, TaskType } from '@crm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheckBig, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { LayoutToggle } from '../components/boards/layout-toggle.tsx';
import { MY_TASKS_QUERY, TaskRow, tasksQuery } from '../components/records/record-sidebar.tsx';
import { TaskBoard } from '../components/tasks/task-board.tsx';
import { useSession } from '../lib/session.tsx';
import { useSettings } from '../lib/settings.ts';
import { TaskCount } from '../components/tasks/task-filter-chip.tsx';
import { TaskGroup } from '../components/tasks/task-group.tsx';
import {
  automatedFor,
  TaskKindChip,
  TaskTypeChip,
  typesFor,
  type TaskKind,
} from '../components/tasks/task-kind-select.tsx';
import { Gate } from '../components/shell/gate.tsx';
import { Button } from '../components/ui/button.tsx';
import { describeError } from '../lib/api.ts';
import { api, type TaskView } from '../lib/client.ts';
import { groupByDue } from '../lib/task-groups.ts';
import { EmptyState } from '../components/ui/empty-state.tsx';
import { PageHeader } from '../components/ui/page-header.tsx';
import { ToggleButton } from '../components/ui/toggle-button.tsx';

/**
 * My Tasks (RELAY-945): everything assigned to me that is not done, by due date, grouped so
 * the overdue ones are the first thing on the page; in progress before to do within a group
 * (TS-1739). Changing a status here is the same PATCH the record sidebar sends; the assignee
 * may always move their own task. The Board layout (TS-1742) has a column per status, done
 * included, and a drag is that same PATCH; which layout is shown is remembered per user. The
 * kind and type filters apply to both layouts; Completed is the list's (the board has Done).
 */
export function MyTasksPage() {
  const qc = useQueryClient();
  const session = useSession();
  const userId = session.me.user.id;
  const [settings, updateSettings] = useSettings(userId);
  const layout = settings.myTasksLayout;
  const board = layout === 'board';
  const [showDone, setShowDone] = useState(false);
  const [type, setType] = useState<TaskType | undefined>();
  const [kind, setKind] = useState<TaskKind>('manual');
  // A type the new kind cannot have would filter to nothing, so it is dropped.
  const changeKind = (next: TaskKind) => {
    setKind(next);
    if (type && !typesFor(next).includes(type)) setType(undefined);
  };
  const status = board ? 'all' : showDone ? 'done' : 'open';
  const automated = automatedFor(kind);
  const filtered = !!type || kind !== 'manual';
  const tasks = useQuery({
    queryKey: [...MY_TASKS_QUERY, status, type, automated],
    queryFn: () => api.tasks.mine(status, type, automated),
    // Only the list reads this query: the board has its own per-column reads.
    enabled: !board,
  });
  const typeCounts = useQuery({
    queryKey: [...MY_TASKS_QUERY, 'type-counts', status, automated],
    queryFn: () => api.tasks.mineTypeCounts(status, automated),
  });
  const [error, setError] = useState<string | null>(null);
  const setStatus = useMutation({
    mutationFn: ({ task, status }: { task: TaskView; status: TaskStatus }) =>
      api.tasks.patch(task.id, { status }),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: MY_TASKS_QUERY });
      await qc.invalidateQueries({ queryKey: tasksQuery(t.entityId, t.entityType) });
    },
    onError: (err) => setError(describeError(err)),
  });

  const data = board ? undefined : tasks.data;
  const groups: Array<{ name: string; items: TaskView[] }> = showDone
    ? [{ name: 'Completed', items: data ?? [] }]
    : groupByDue(data ?? []);

  return (
    <Gate permission="organisation.read">
      <>
        <PageHeader
          title="My Tasks"
          description={
            board
              ? 'Everything assigned to you, by status. Drag a card to change it.'
              : 'Everything assigned to you, by when it is due.'
          }
          actions={
            <LayoutToggle
              layout={layout}
              tableLabel="List"
              onChange={(next) =>
                updateSettings({ myTasksLayout: next === 'board' ? 'board' : 'table' })
              }
            />
          }
        />
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="my-task-filters">
          {!board && (
            <ToggleButton
              pressed={showDone}
              onClick={() => setShowDone((v) => !v)}
              label="Completed"
              data-testid="toggle-done"
            />
          )}
          <TaskKindChip kind={kind} onChange={changeKind} />
          <TaskTypeChip kind={kind} type={type} onChange={setType} counts={typeCounts.data} />
          {data && <TaskCount count={data.length} />}
          {filtered && (
            <Button
              size="sm"
              variant="link"
              data-testid="clear-filters"
              onClick={() => {
                setType(undefined);
                setKind('manual');
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
        {board && (
          <TaskBoard
            name="my-tasks"
            scope={[userId, type, automated]}
            load={(s) => api.tasks.mine(s, type, automated)}
            // Everything here is the caller's own task, and the assignee may always move it.
            canMove={() => true}
            canDelete={(t) => t.createdBy?.id === userId}
          />
        )}
        {error && (
          <p className="text-danger mb-3 text-sm" role="alert">
            {error}
          </p>
        )}
        {!board && tasks.isPending && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        )}
        {!board && tasks.error != null && (
          <p className="text-danger text-sm" role="alert">
            {describeError(tasks.error)}
          </p>
        )}
        {data?.length === 0 && (
          <EmptyState
            icon={CircleCheckBig}
            data-testid="no-tasks"
            title={
              filtered
                ? 'No tasks match these filters.'
                : showDone
                  ? 'Nothing completed yet.'
                  : 'Nothing assigned to you. Nice.'
            }
            description={
              filtered
                ? 'Try another kind or type.'
                : showDone
                  ? 'Tasks you finish show up here.'
                  : 'Tasks assigned to you on candidates, projects and jobs show up here.'
            }
          />
        )}
        <div className="flex flex-col gap-6">
          {groups
            .filter((g) => g.items.length > 0)
            .map((g) => (
              <TaskGroup key={g.name} name={g.name} count={g.items.length}>
                {g.items.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    onStatusChange={(status) => setStatus.mutate({ task: t, status })}
                    canToggle
                    // Only the creator here: My Tasks does not know who can write to the record.
                    canDelete={t.createdBy?.id === userId}
                    showRecord
                  />
                ))}
              </TaskGroup>
            ))}
        </div>
      </>
    </Gate>
  );
}
